<template>
  <div class="list-view list-view--stack-wide list-view--admin-workflows">
    <div class="list-view__header">
      <div>Title</div>
      <div>Colour</div>
      <div>Workflow Type</div>
      <div class="list-view__small-column">Published</div>
      <div class="list-view__small-column">Global</div>
      <div>Actions</div>
    </div>

    <div class="list-view__row" v-for="workflow in workflows" :key="workflow.id">
      <div>
        <router-link :to="workflowScreensRoute(workflow)">
          {{ workflow.title }}
        </router-link>
        <span class="text-small" v-if="hasOverridenAttribute(workflow, 'title')">
          &nbsp; ({{ workflow.original_title }})
        </span>
      </div>
      <div>
        <span class="colour" :style="{ backgroundColor: workflow.colour }"></span> {{ workflow.colour }}
        <span class="text-small" v-if="hasOverridenAttribute(workflow, 'colour')">
          &nbsp; ({{ workflow.original_colour }})
        </span>
      </div>
      <div>{{ workflowType({ workflow, fullTitle: true }) }} ({{ workflow.workflow_type }})</div>
      <div class="list-view__small-column">
        <span class="show-for-small">Published: </span>
        <i class="fa-solid fa-check" v-if="workflow.published" />
        <i class="fa-solid fa-xmark" v-else />
      </div>
      <div class="list-view__small-column">
        <span class="show-for-small">Global: </span>
        <i class="fa-solid fa-check" v-if="workflow.global" />
        <i class="fa-solid fa-xmark" v-else />
      </div>
      <div class="list-view__center-column list-actions list-actions--three-rows">
        <router-link :to="editWorkflowRoute(workflow)" class="list-action">
          <i class="fa-solid fa-pen-to-square" />
          <span class="list-action__label">Edit</span>
        </router-link>
        <router-link
          :to="workflowPreviewRoute(workflow)"
          class="list-action"
        >
          <i class="fa-solid fa-file-magnifying-glass" />
          <span class="list-action__label">Preview</span>
        </router-link>
        <button
          class="icon-button list-action"
          @click="cloneWorkflow(workflow)"
          :disabled="!!workflow.cloning"
        >
          <i class="fa-solid fa-copy" />
          <span class="list-action__label">Clone</span>
        </button>
        <button
          class="icon-button list-action"
          @click="restoreWorkflow(workflow)"
          :disabled="!!!workflow.original_id"
        >
          <i class="fa-solid fa-rotate-left" />
          <span class="list-action__label">Restore</span>
        </button>
        <button
          class="icon-button list-action"
          @click="deleteWorkflow(workflow)"
          :disabled="!canDeleteWorkflow(workflow)"
        >
          <i class="fa-solid fa-trash" />
          <span class="list-action__label">Delete</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Common from '@/common';
import PulseLoader from '@/components/PulseLoader';

export default {
  name: 'admin-workflows-list',

  props: ['workflows'],

  components: { PulseLoader },

  computed: {
    ...mapState({
      primaryColour: state => state.primaryColour,
    }),

    ...mapGetters([
      'workflowType',
    ]),

    ...mapGetters('admin', [
      'lastSearchTerm',
    ]),

    contextCompanyId() {
      return parseInt(this.$route.params.companyId, 10) || parseInt(this.$route.query.companyId, 10) || 0;
    },

    contextQuery() {
      if (!this.contextCompanyId) return null;
      const term = this.$route.query.term || this.lastSearchTerm;
      const query = { companyId: this.contextCompanyId };
      if (term) query.term = term;
      return query;
    },
  },

  methods: {
    cloneWorkflow(workflow) {
      // eslint-disable-next-line no-alert
      const clonedTitle = prompt('Cloned workflow name:', `${workflow.title} (cloned)`);
      if (clonedTitle) {
        this.$store.dispatch('admin/cloneWorkflow', {
          companyId: this.contextCompanyId || null,
          workflowId: workflow.id,
          clonedTitle,
        });
      }
    },

    restoreWorkflow(workflow) {
      Common.confirmDialog({
        modal: this.$modal,
        title: `Are you sure you want to restore the ${workflow.title} workflow?`,
        // eslint-disable-next-line max-len
        text: 'This will restore the workflow to the original rradar template, therefore losing any customisations.<br><br>You cannot restore custom workflows that have existing incidents.',
        okHandler: () => {
          this.$store.dispatch('admin/restoreWorkflow', {
            companyId: this.contextCompanyId || null,
            workflowId: workflow.id,
          });
        },
      });
    },

    hasOverridenAttribute(workflow, attribute) {
      return workflow[`original_${attribute}`] && workflow[attribute] !== workflow[`original_${attribute}`];
    },

    editWorkflowRoute(workflow) {
      const routeParams = {};
      let routeName = 'admin-edit-workflow';
      const route = {};

      if (workflow.global && this.contextCompanyId) {
        routeName = 'admin-edit-company-workflow';
        routeParams.companyId = this.contextCompanyId;
        routeParams.workflowId = workflow.id;
      } else {
        routeParams.globalWorkflowId = workflow.id;
        if (this.contextQuery) route.query = this.contextQuery;
      }

      route.name = routeName;
      route.params = routeParams;
      return route;
    },

    workflowScreensRoute(workflow) {
      const route = {
        name: 'admin-screens',
        params: { globalWorkflowId: workflow.id },
      };
      if (this.contextQuery) route.query = this.contextQuery;
      return route;
    },

    workflowPreviewRoute(workflow) {
      const route = {
        name: 'admin-preview-workflow',
        params: { globalWorkflowId: workflow.id },
      };
      if (this.contextQuery) route.query = this.contextQuery;
      return route;
    },

    canDeleteWorkflow(workflow) {
      return (workflow.global && !this.contextCompanyId) || !workflow.global;
    },

    deleteWorkflow(workflow) {
      Common.confirmDialog({
        modal: this.$modal,
        title: `Are you sure you want to delete the ${workflow.title} workflow?`,
        // eslint-disable-next-line max-len
        text: 'This will delete the main workflow, therefore deleting it for any company that has access to it.<br><br>You cannot delete workflows that have existing incidents.',
        okHandler: () => {
          this.$store.dispatch('admin/deleteWorkflow', workflow.workflow_id || workflow.id);
        },
      });
    },
  },
};
</script>
